import React, { useEffect, useState } from "react";
import { MessageSquareWarning, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { useMyRestaurant } from "./RestaurantOrders";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import { restaurantApi, formatDate, errorMessage } from "../../lib/api";

export default function Complaints() {
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => restaurantApi.complaints().then(setData).catch(() => setData({ count: 0, complaints: [] }));
  useEffect(() => { if (restaurant) load(); }, [restaurant]);

  const resolve = async (c) => {
    setBusyId(c.order_id);
    try { await restaurantApi.resolveComplaint(c.order_id); toast.success("Marked resolved"); await load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };

  if (restaurant === undefined) return <><PageHeader title="Complaints" /><LoadingBlock rows={3} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" description="Create your restaurant profile first." /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;

  return (
    <>
      <PageHeader title="Complaints & feedback" description="Low ratings with a comment land here so you can make it right." />
      {data === null && <LoadingBlock rows={3} />}
      {data && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <StatCard label="Open complaints" value={data.open} icon={MessageSquareWarning} tone="brand" />
            <StatCard label="Total received" value={data.count} icon={MessageSquareWarning} />
          </div>
          {data.count === 0 && <EmptyState icon={CheckCircle2} title="No complaints" text="Great work — no customer complaints to review." />}
          <div className="grid gap-3" data-testid="complaints-list">
            {(data.complaints || []).map((c) => (
              <div key={c.order_id} className={`kt-card p-4 ${c.complaint_resolved ? "opacity-70" : "border-l-4 border-l-red-400"}`} data-testid={`complaint-${c.order_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700"><Star className="h-3 w-3 fill-red-700" /> {c.rating}★</span>
                    <span className="font-semibold text-ink">{c.customer_name}</span>
                  </div>
                  <span className="text-xs text-ink-muted">{formatDate(c.rated_at)}</span>
                </div>
                <p className="mt-2 rounded-xl bg-surface-100 p-3 text-sm text-ink">{c.review}</p>
                <p className="mt-1 text-xs text-ink-muted">Order #{c.order_id.slice(-6).toUpperCase()} · {c.items.join(", ")}</p>
                <div className="mt-3">
                  {c.complaint_resolved
                    ? <span className="inline-flex items-center gap-1.5 text-sm text-graphite"><CheckCircle2 className="h-4 w-4" /> Resolved</span>
                    : <Button size="sm" onClick={() => resolve(c)} disabled={busyId === c.order_id} className="rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid={`resolve-${c.order_id}`}><CheckCircle2 className="mr-2 h-4 w-4" /> Mark resolved</Button>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
